/**
 * Resource placement.
 *
 * Three constraints pull against each other:
 *
 *   Terrain — each resource may only appear where its content entry allows.
 *   Spacing — resources of the same kind must not clump, or one civilisation starts
 *             on all the iron in the world and the strategic layer is decided at turn one.
 *   Guarantees — every strategic resource must exist somewhere. A map with no horses
 *             is not a hard map, it is a broken one, and a player can lose hours
 *             before discovering it.
 *
 * The pass therefore places to a target count, then makes a second attempt for any
 * strategic resource that fell short, relaxing spacing rather than terrain. Terrain
 * rules are content and must hold; spacing is only an aesthetic preference.
 */

import type { Content, ResourceDef } from '../../../content/schema/index.js';
import { shuffle, type RngState } from '../rng/index.js';
import type { TileGrid } from '../state/types.js';
import { indexToHex } from '../state/grid.js';
import { hexDistance } from '../hex/index.js';
import type { MapgenParams } from './params.js';

export interface ResourceReport {
  placed: Record<string, number>;
  /** Strategic resources that could not reach their guaranteed minimum. */
  shortfalls: string[];
  total: number;
}

/** Weight of each class in the overall budget. Strategics are rarer than bonuses by design. */
const CLASS_WEIGHT: Record<ResourceDef['class'], number> = {
  strategic: 0.85,
  luxury: 0.75,
  bonus: 1.4,
};

export function placeResources(
  grid: TileGrid,
  content: Content,
  isLand: Uint8Array,
  params: MapgenParams,
  rng: RngState,
): ResourceReport {
  const tiles = grid.terrain.length;

  let landTiles = 0;
  for (let i = 0; i < tiles; i++) if (isLand[i] === 1) landTiles++;

  // Candidate tiles per resource, from the content's own terrain rules.
  const candidatesFor = (resource: ResourceDef & { id: number }): number[] => {
    const allowed = new Set(resource.validTerrain);
    const out: number[] = [];
    for (let i = 0; i < tiles; i++) {
      if (grid.resource[i] !== 0) continue;
      if (allowed.has(content.terrain.get(grid.terrain[i]!).key)) out.push(i);
    }
    return out;
  };

  const placed: Record<string, number> = {};
  const shortfalls: string[] = [];
  let total = 0;

  // Placed positions per resource, for the spacing check. Kept as hex coordinates
  // because spacing must be measured in hexes, not in array distance — which wraps
  // and would let two "distant" resources sit side by side across the seam.
  const placedHexes = new Map<number, Array<{ q: number; r: number }>>();

  const tryPlace = (resource: ResourceDef & { id: number }, count: number, minSpacing: number): number => {
    if (count <= 0) return 0;
    const candidates = shuffle(rng, candidatesFor(resource));
    const existing = placedHexes.get(resource.id) ?? [];
    let done = 0;

    for (const index of candidates) {
      if (done >= count) break;
      if (grid.resource[index] !== 0) continue;

      const here = indexToHex(grid, index);
      if (minSpacing > 0) {
        let tooClose = false;
        for (const other of existing) {
          if (hexDistance(here, other) < minSpacing) {
            tooClose = true;
            break;
          }
        }
        if (tooClose) continue;
      }

      grid.resource[index] = resource.id;
      existing.push(here);
      done++;
    }

    placedHexes.set(resource.id, existing);
    return done;
  };

  // Strategics first: they have the tightest terrain rules and the hardest guarantee,
  // so they should get first pick of the tiles rather than the leftovers.
  const ordered = [...content.resources.all]
    .filter((r) => r.key !== 'none')
    .sort((a, b) => CLASS_WEIGHT[a.class] - CLASS_WEIGHT[b.class]);

  // `resourceDensity` is the fraction of land tiles that should carry a resource
  // ALTOGETHER, shared out in proportion to class weight and rarity. Treating it as a
  // per-resource figure — as this first did — multiplies the budget by the number of
  // resource types and carpets the map.
  const totalBudget = landTiles * params.resourceDensity;
  const weightOf = (r: ResourceDef): number => CLASS_WEIGHT[r.class] * r.rarity;
  const weightSum = ordered.reduce((sum, r) => sum + weightOf(r), 0);

  for (const resource of ordered) {
    const share = weightSum > 0 ? weightOf(resource) / weightSum : 0;
    const target = Math.max(0, Math.round(totalBudget * share));

    let count = tryPlace(resource, target, params.resourceMinSpacing);

    // Guarantee pass: relax spacing, never terrain.
    if (resource.class === 'strategic' && count < params.minStrategicPerType) {
      count += tryPlace(resource, params.minStrategicPerType - count, 0);
      if (count < params.minStrategicPerType) shortfalls.push(resource.key);
    }

    placed[resource.key] = count;
    total += count;
  }

  return { placed, shortfalls, total };
}
